/**
 * Mein Tag – Startseite.
 *
 * Drei Blöcke, keine Navigationskacheln:
 *   1. Begrüßung
 *   2. "Auf deinem Tisch" – die Kennzahl ist exakt die Länge der Liste darunter
 *   3. "Team-Überblick" – nur für die Geschäftsführung
 * Darunter die Fußzeile mit Version und Versionshistorie.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { IconInfo, IconSchloss } from '../komponenten/Icons';
import { dat, tageSeit } from '../hilfen/format';
import type { Angebot } from '../typen';
import './meintag.css';

type Zeitraum = 'woche' | 'monat' | 'ab';

export function MeinTag() {
  const { nutzer, modalOeffnen, darf } = useKora();
  const meta = useMeta();
  const navigate = useNavigate();

  const [autoZeitraum, setAutoZeitraum] = useState<Zeitraum>('woche');
  const [autoAb, setAutoAb] = useState('');
  const [alleMails, setAlleMails] = useState(false);
  const [teamPerson, setTeamPerson] = useState<string | null>(null);
  const [teamZeitraum, setTeamZeitraum] = useState<Zeitraum>('woche');
  const [teamAb, setTeamAb] = useState('');

  const angeboteAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
    enabled: darf('monitor'),
  });

  const angebote = useMemo(() => angeboteAbfrage.data ?? [], [angeboteAbfrage.data]);
  const meinName = nutzer?.name ?? '';

  /** Liegt das Datum im gewählten Zeitraum? */
  const imZeitraum = useMemo(
    () => (iso: string, modus: Zeitraum, ab: string) => {
      if (modus === 'ab') return !!ab && iso >= ab;
      if (modus === 'monat') return iso.slice(0, 7) === meta.heute.slice(0, 7);
      return tageSeit(iso, meta.heute) <= 7;
    },
    [meta.heute],
  );

  /**
   * "Meine" Vorgänge: die, die ich führe – Versender, ersatzweise Kalkulant.
   *
   * Dieselbe Regel wie im Team-Überblick weiter unten und im Cockpit. Der
   * Prototyp zählte hier nur über den Kalkulanten; in den Demo-Daten war das
   * dieselbe Person, mit echten Daten stand dann "0 Vorgänge warten auf dich"
   * direkt über "Martin · 16 offen".
   */
  const meine = useMemo(
    () => angebote.filter((a) => (a.versender || a.kalkulant) === meinName),
    [angebote, meinName],
  );

  /**
   * Was heute wirklich Handarbeit braucht – Anruf oder fällige Wiedervorlage.
   *
   * Bewusst getrennt von der angezeigten Liste: der Prototyp schnitt schon beim
   * Filtern auf fünf ab und zählte dann die Liste. Die große Zahl war damit auf
   * fünf gedeckelt, während der Team-Überblick daneben die echte Zahl nannte.
   */
  const meineDran = useMemo(
    () => meine.filter((a) => a.nachfass.anrufFaellig || a.wvFaellig),
    [meine],
  );

  const MAX_LISTE = 5;
  const meineFaellig = meineDran.slice(0, MAX_LISTE);
  const weitere = meineDran.length - meineFaellig.length;

  /** Offene Vorgänge insgesamt, für die Einordnung der Null. */
  const meineOffen = useMemo(() => meine.filter((a) => a.offen).length, [meine]);

  const autoMails = useMemo(() => {
    const liste: { id: number; iso: string; name: string; zeit: string }[] = [];
    for (const a of meine) {
      for (const e of a.erinnerungen) {
        if (e.art !== 'auto' || !imZeitraum(e.datum, autoZeitraum, autoAb)) continue;
        liste.push({
          id: a.id,
          iso: e.datum,
          name: a.name,
          zeit: `${e.stufe}. Erinnerung · ${e.wochentag}, ${e.zeit || '—'}`,
        });
      }
    }
    liste.sort((x, y) => (x.iso < y.iso ? 1 : x.iso > y.iso ? -1 : 0));
    return liste;
  }, [meine, autoZeitraum, autoAb, imZeitraum]);

  const autoTitel =
    autoZeitraum === 'ab'
      ? autoAb
        ? `Automatisch versendet seit ${dat(autoAb)}`
        : 'Automatisch versendet · Datum wählen'
      : autoZeitraum === 'monat'
        ? 'Diesen Monat automatisch versendet'
        : 'Diese Woche automatisch versendet';

  const autoLeerText =
    autoZeitraum === 'woche'
      ? 'Diese Woche ging noch keine automatische Erinnerung raus.'
      : 'In diesem Zeitraum ging keine automatische Erinnerung raus.';

  const sichtbareMails = alleMails ? autoMails : autoMails.slice(0, 4);

  // --- Team-Überblick --------------------------------------------------------
  const istFuehrung = !!nutzer?.istAdmin;

  const teamUeberblick = useMemo(() => {
    if (!istFuehrung) return [];
    const offenAlle = angebote.filter((a) => a.offen);
    return meta.team
      .filter((p) => p.imVertrieb)
      .map((p) => {
        const seine = offenAlle.filter((a) => (a.versender || a.kalkulant) === p.name);
        const krit = seine.filter((a) => a.nachfass.anrufFaellig || a.wvFaellig).length;
        return { ...p, anzahl: seine.length, krit };
      })
      .filter((t) => t.anzahl > 0);
  }, [istFuehrung, angebote, meta.team]);

  const teamMails = useMemo(() => {
    if (!teamPerson) return [];
    const liste: { id: number; iso: string; name: string; zeit: string }[] = [];
    for (const a of angebote) {
      if ((a.versender || a.kalkulant) !== teamPerson) continue;
      for (const e of a.erinnerungen) {
        if (!imZeitraum(e.datum, teamZeitraum, teamAb)) continue;
        liste.push({
          id: a.id,
          iso: e.datum,
          name: a.name,
          zeit: `${e.stufe}. Erinnerung · ${e.wochentag}, ${e.datumText} · ${e.zeit || '—'}`,
        });
      }
    }
    liste.sort((x, y) => (x.iso < y.iso ? 1 : x.iso > y.iso ? -1 : 0));
    return liste;
  }, [teamPerson, angebote, teamZeitraum, teamAb, imZeitraum]);

  const teamMailTitel =
    teamZeitraum === 'ab'
      ? teamAb
        ? `Versendet seit ${dat(teamAb)}`
        : 'Versendet · Datum wählen'
      : teamZeitraum === 'monat'
        ? 'Diesen Monat versendet'
        : 'Diese Woche versendet';

  return (
    <div className="eh-inhalt--schmal eh-inhalt">
      <div className="eh-gruss">Willkommen, {nutzer?.name}</div>

      {darf('monitor') ? (
        <div className="tisch">
          <div className="tisch-kopf">
            <div className="tisch-kicker">Auf deinem Tisch</div>
            <div className="tisch-zahl-zeile">
              <span
                className="tisch-zahl"
                style={{ color: meineDran.length ? 'var(--eh-rot)' : 'var(--eh-text-4)' }}
              >
                {meineDran.length}
              </span>
              {/*
                Die Zahl allein war missverständlich: daneben stand im
                Team-Überblick "16 offen", und beides hieß "offen". Gemeint sind
                zwei verschiedene Mengen – alle beim Kunden liegenden Vorgänge
                und die Teilmenge, die heute eine Handlung braucht. Deshalb hier
                das Verhältnis statt einer nackten Zahl.
              */}
              <span className="tisch-text">
                {meineOffen > 0
                  ? `von ${meineOffen} Vorgängen beim Kunden ${
                      meineDran.length === 1 ? 'braucht' : 'brauchen'
                    } heute eine Handlung`
                  : meineDran.length === 1
                    ? 'Vorgang wartet auf dich'
                    : 'Vorgänge warten auf dich'}
              </span>
            </div>
          </div>

          <div className="tisch-liste">
            {meineFaellig.map((a) => {
              const letzteNotiz = a.anrufe[0]?.notiz ?? '';
              return (
                <button
                  key={a.id}
                  type="button"
                  className="tisch-eintrag"
                  onClick={() => navigate(`/angebot/${a.id}`, { state: { herkunft: 'meintag' } })}
                >
                  <span className="tisch-eintrag-name">
                    {a.name}
                    {a.wvFaellig && letzteNotiz ? ` · ${letzteNotiz}` : ''}
                  </span>
                  <span className={`tisch-badge ${a.wvFaellig ? 'gelb' : 'rot'}`}>
                    {a.wvFaellig ? 'Wiedervorlage fällig' : 'Anruf ausstehend'}
                  </span>
                </button>
              );
            })}
            {weitere > 0 && (
              <button
                type="button"
                className="tisch-weitere"
                onClick={() => navigate(`/cockpit?versender=${encodeURIComponent(meinName)}`)}
              >
                {weitere === 1
                  ? 'Ein weiterer Anruf ist fällig'
                  : `${weitere} weitere Anrufe sind fällig`}{' '}
                – alle im Cockpit ansehen
              </button>
            )}

            {/* Sagt, was die Zahl oben NICHT enthält – sonst liest sich der
                Unterschied zwischen 9 und 16 wie ein Widerspruch. */}
            {meineDran.length > 0 && meineOffen > meineDran.length && (
              <div className="tisch-rest">
                Die übrigen {meineOffen - meineDran.length} liegen beim Kunden und sind noch nicht
                dran.
              </div>
            )}

            {/* Der Prototyp schrieb hier "Gerade nichts offen – gut so." Das
                behauptet mehr, als die Zahl hergibt: gezählt werden nur Anrufe
                und Wiedervorlagen, nicht die laufenden Vorgänge. */}
            {meineDran.length === 0 && (
              <div className="tisch-leer">
                {meineOffen > 0
                  ? `Kein Anruf und keine Wiedervorlage fällig. ${meineOffen} ${
                      meineOffen === 1 ? 'Vorgang läuft' : 'Vorgänge laufen'
                    } beim Kunden.`
                  : 'Gerade nichts offen – gut so.'}
              </div>
            )}
          </div>

          <div className="tisch-mails">
            <div className="tisch-mails-kopf">
              <div className="tisch-mails-titel">{autoTitel}</div>
              <ZeitraumWahl
                zeitraum={autoZeitraum}
                ab={autoAb}
                setzeZeitraum={setAutoZeitraum}
                setzeAb={setAutoAb}
                variante="warm"
              />
            </div>

            {sichtbareMails.map((m, i) => (
              <button
                key={`${m.id}-${m.iso}-${i}`}
                type="button"
                className="tisch-mail"
                onClick={() => navigate(`/angebot/${m.id}`, { state: { herkunft: 'meintag' } })}
              >
                <span className="tisch-mail-name">{m.name}</span>
                <span className="tisch-mail-zeit">{m.zeit}</span>
              </button>
            ))}

            {autoMails.length === 0 && <div className="tisch-mail-leer">{autoLeerText}</div>}

            {autoMails.length > 4 && (
              <button type="button" className="tisch-mehr" onClick={() => setAlleMails((a) => !a)}>
                {alleMails ? 'Weniger anzeigen' : `Alle ${autoMails.length} anzeigen`}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="eh-karte">
          <div className="eh-hinweis">
            Für das Vertriebscockpit fehlt dir die Freigabe. Die Geschäftsführung kann sie im Admin
            Center erteilen.
          </div>
        </div>
      )}

      {istFuehrung && (
        <div className="team">
          <div className="team-kopf">
            <span className="team-titel">Team-Überblick</span>
            <span
              title="Nur Geschäftsführung und Kaufmännisches"
              style={{ color: 'var(--eh-rot)', display: 'flex' }}
            >
              <IconSchloss />
            </span>
          </div>

          <div className="team-liste">
            {teamUeberblick.map((t) => (
              <div key={t.name} style={{ display: 'flex', flexDirection: 'column' }}>
                <button
                  type="button"
                  className={`team-zeile ${teamPerson === t.name ? 'offen' : ''}`}
                  onClick={() => setTeamPerson((p) => (p === t.name ? null : t.name))}
                >
                  <span
                    className="eh-avatar team-avatar"
                    style={t.profilbild ? { backgroundImage: `url(${t.profilbild})` } : undefined}
                  >
                    {t.profilbild ? '' : t.initialen}
                  </span>
                  <span className="team-name">{t.name}</span>
                  {/* „offen" hieß hier und in der Kachel oben zweierlei.
                      Jetzt: beim Kunden = liegt draußen, Handlung = jetzt dran. */}
                  <span className="team-anzahl">{t.anzahl} beim Kunden</span>
                  <span className="team-krit">
                    {t.krit ? `${t.krit} ${t.krit === 1 ? 'braucht' : 'brauchen'} eine Handlung` : ''}
                  </span>
                  <span className="team-pfeil">{teamPerson === t.name ? '▲' : '▼'}</span>
                </button>

                {teamPerson === t.name && (
                  <div className="team-detail">
                    <div className="team-detail-kopf">
                      <span className="team-detail-titel">{teamMailTitel}</span>
                      <ZeitraumWahl
                        zeitraum={teamZeitraum}
                        ab={teamAb}
                        setzeZeitraum={setTeamZeitraum}
                        setzeAb={setTeamAb}
                        variante="kalt"
                      />
                    </div>

                    <div className="team-detail-mails">
                      {teamMails.map((m, i) => (
                        <button
                          key={`${m.id}-${m.iso}-${i}`}
                          type="button"
                          className="tisch-mail"
                          onClick={() => navigate(`/angebot/${m.id}`, { state: { herkunft: 'meintag' } })}
                        >
                          <span className="tisch-mail-name">{m.name}</span>
                          <span className="tisch-mail-zeit">{m.zeit}</span>
                        </button>
                      ))}
                      {teamMails.length === 0 && (
                        <div className="team-mail-leer">
                          In diesem Zeitraum keine Erinnerung versendet.
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      className="tisch-mehr"
                      onClick={() => navigate(`/cockpit?versender=${encodeURIComponent(t.name)}`)}
                    >
                      Offene Vorgänge im Cockpit ansehen →
                    </button>
                  </div>
                )}
              </div>
            ))}

            {teamUeberblick.length === 0 && (
              <div className="team-leer">Niemand hat gerade etwas beim Kunden liegen.</div>
            )}
          </div>
        </div>
      )}

      <div className="tag-fuss">
        <span>
          Kora {meta.version}
          {meta.demoModus ? ' · Demo-Modus' : ''}
          {meta.trockenlauf ? ' · Testbetrieb, Kundenmails werden nicht versendet' : ''}
        </span>
        <button
          type="button"
          className="tag-info"
          title="Was ist neu – Versionshistorie"
          onClick={() => modalOeffnen({ art: 'changelog', alle: true })}
        >
          <IconInfo groesse={14} />
        </button>
      </div>
    </div>
  );
}

function ZeitraumWahl({
  zeitraum,
  ab,
  setzeZeitraum,
  setzeAb,
  variante,
}: {
  zeitraum: Zeitraum;
  ab: string;
  setzeZeitraum: (z: Zeitraum) => void;
  setzeAb: (s: string) => void;
  variante: 'warm' | 'kalt';
}) {
  const chips: { k: Zeitraum; l: string }[] = [
    { k: 'woche', l: 'Diese Woche' },
    { k: 'monat', l: 'Dieser Monat' },
  ];
  return (
    <div className={`zeitraum zeitraum--${variante}`}>
      {chips.map((c) => (
        <button
          key={c.k}
          type="button"
          className={`zeitraum-chip ${zeitraum === c.k ? 'aktiv' : ''}`}
          onClick={() => setzeZeitraum(c.k)}
        >
          {c.l}
        </button>
      ))}
      <input
        type="date"
        value={ab}
        title="Ab Datum"
        className={`zeitraum-datum ${zeitraum === 'ab' ? 'aktiv' : ''}`}
        onChange={(e) => {
          setzeAb(e.target.value);
          setzeZeitraum('ab');
        }}
      />
    </div>
  );
}
